import React, { useCallback } from 'react';
import { useModals } from '../../../SpinModalProvider';
import { BaseRouteModal } from '../../BaseRouteModal';
import { GamesSelectComponent } from '../../../../forms/GameSelectComponent';
import { useAppState } from '../../../../../client/providers/AppProvider';
import { useFormWithSchema } from '../../../../../client/hooks/use-form-schema';
import { toast } from 'react-toastify';
import { InputField } from '../../../../input/InputField';
import { gql } from '@urql/core';
import { useAuthenticatedMutation } from '../../../../../client/hooks';
import invariant from 'tiny-invariant';
import {
    CreatePlayerTeamActionDocument,
    CreatePlayerTeamActionMutation,
    CreatePlayerTeamActionMutationVariables,
    Espin_Role_Enum,
} from '../../../../../generated/spin-graphql';
import { ActionSuccessError } from '../../../../../types/shared.types';
import { PlayerInviteOptions } from './InvitePlayerOptions';
import { CreateTeamFormData, CreateTeamFormDataSchema } from './HighSchoolCreateTeamPopup';

// TODO - restrict to non-deleted players!
gql`
    mutation CreatePlayerTeamAction($input: CreatePlayerTeamInput!) {
        createPlayerTeam(input: $input) {
            id
            notFound
            status
            comments
        }
    }
`;

function CreateTeamForm() {
    const [_, createTeam] = useAuthenticatedMutation<
        CreatePlayerTeamActionMutation,
        CreatePlayerTeamActionMutationVariables
    >(CreatePlayerTeamActionDocument);

    const { user } = useAppState();
    const {
        register,
        handleSubmit,
        control,
        watch,
        setError,
        formState: { isSubmitting, isDirty, errors },
    } = useFormWithSchema(CreateTeamFormDataSchema);
    const { closeActiveModal } = useModals();
    const onSubmit = useCallback(
        async (values: CreateTeamFormData) => {
            invariant(user.role === Espin_Role_Enum.EspinPlayerUser, 'User is not a player.'); // User lack rizz

            const { team_name, game, players_to_invite } = values;
            const result = await createTeam(
                {
                    input: {
                        name: team_name,
                        primary_game: game.value,
                        player_profile_id: user.player_profile.id,
                        members: players_to_invite,
                    },
                },
                { additionalTypenames: ['espin_team', 'espin_team_invite'] },
            );

            if (result.error) {
                toast.error(`There was an error adding your team. Check details and try again`);
                console.error(result.error);
                return;
            }
            // check ze result to notify which team members failed to add
            const { status, notFound, comments, id } = result.data?.createPlayerTeam;
            if (status === ActionSuccessError.SUCCESS) {
                toast.success('Team added!');
                closeActiveModal();
            } else {
                if (notFound && notFound.length > 0) {
                    setError('players_to_invite', {
                        type: 'custom',
                        message: `User(s) not found! Please be sure to only invite players with active accounts. If they don't have an account, have them make one first, then you can invite them to your team.`,
                    });
                } else if (id === 'Duplicate Team Name') {
                    toast.error(`That team name already exists`);
                } else if (comments) {
                    toast.error(comments);
                } else {
                    // parse out other things here
                    toast.error(`There was an error editing your team`);
                }

                return;
            }
        },
        [user.role, user.player_profile.id, createTeam, closeActiveModal, setError],
    );

    return (
        <>
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit(onSubmit)(e);
                }}
                className="create-team-content"
            >
                <GamesSelectComponent
                    error={errors.game?.value?.message}
                    control={control}
                    isMulti={false}
                    required={true}
                    placeholder={'Select a game'}
                    name="game"
                />
                <InputField
                    error={errors.team_name?.message}
                    htmlFor={'team_name'}
                    name={'team_name'}
                    label="Team Name"
                    placeholder="Team Name"
                    required={true}
                    register={register}
                />
                <PlayerInviteOptions control={control} required={true} />
                <div className="button-container">
                    <button disabled={isSubmitting} className="button medium-button blue-button">
                        Add Team
                    </button>
                </div>
            </form>
        </>
    );
}

export function PlayerCreateTeamPopup() {
    const { closeActiveModal } = useModals();
    return (
        <BaseRouteModal
            className="create-team-popup"
            content={<CreateTeamForm />}
            description=""
            title="Create Team"
            onCancel={() => closeActiveModal()}
        />
    );
}
